import { useEffect, useMemo, useState } from "react";
import { LocateFixed, Search } from "lucide-react";
import { API } from "@/lib/api";
import { haversineKm } from "@/lib/hours";
import Seo from "@/components/Seo";
import LocationCard from "@/components/LocationCard";

export default function Standorte() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [userPos, setUserPos] = useState(null);
  const [geoMessage, setGeoMessage] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    API.get("/locations")
      .then((res) => {
        setLocations(res.data);
        window.__swnPageReady = true;
      })
      .catch(() => {
        setLocations([]);
        window.__swnPageError = true;
      })
      .finally(() => setLoading(false));
  }, []);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setGeoMessage("Dein Browser unterstützt keine Standortabfrage. Die Suche funktioniert trotzdem.");
      return;
    }
    setGeoLoading(true);
    setGeoMessage("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoLoading(false);
      },
      () => {
        setGeoMessage("Standortzugriff nicht möglich oder abgelehnt – die Suche per PLZ/Ort funktioniert weiterhin.");
        setGeoLoading(false);
      },
      { timeout: 10000 },
    );
  };

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = locations.map((loc) => ({
      ...loc,
      _distance: userPos && loc.lat != null && loc.lng != null ? haversineKm(userPos.lat, userPos.lng, loc.lat, loc.lng) : null,
    }));
    if (q) {
      list = list.filter(
        (loc) =>
          loc.city.toLowerCase().includes(q) ||
          loc.name.toLowerCase().includes(q) ||
          (loc.postal_code || "").toLowerCase().startsWith(q),
      );
    }
    if (userPos) list.sort((a, b) => (a._distance ?? Infinity) - (b._distance ?? Infinity));
    return list;
  }, [locations, query, userPos]);

  return (
    <>
      <Seo
        title="Standorte & Finder | Sportwetten NRW × Tipwin"
        description="Alle Tipwin-Wettvermittlungsstellen der Sportwetten NRW GmbH: Suche per PLZ oder Ort, Öffnungszeiten, Anfahrt und Kontakt zu jedem Standort."
        path="/standorte"
      />
      <section className="bg-[#FAFAFA]" data-testid="finder-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E11D2A]">Standortfinder</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-[#1A1A1A] sm:text-4xl lg:text-5xl">
            Finde deine Filiale
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#52525B]">
            Suche per PLZ oder Ort – oder nutze auf Wunsch deinen Gerätestandort. Dein Standort wird
            erst abgefragt, wenn du es ausdrücklich möchtest.
          </p>
        </div>

        <div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <label className="relative flex-1">
              <span className="sr-only">PLZ oder Ort suchen</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#52525B]" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="PLZ oder Ort eingeben, z. B. 59227 oder Rietberg"
                className="min-h-[48px] w-full rounded-lg border border-[#E4E4E7] bg-white pl-12 pr-4 text-base text-[#1A1A1A] placeholder:text-[#96908B] focus:border-[#E11D2A]/60 focus:outline-none"
                data-testid="finder-search-input"
              />
            </label>
            <button
              type="button"
              onClick={requestLocation}
              disabled={geoLoading}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-[#E4E4E7] bg-white px-5 py-3 text-sm font-semibold text-[#1A1A1A] transition-colors hover:border-[#E11D2A]/50 hover:bg-[#F4F4F5] disabled:opacity-60"
              data-testid="finder-geolocation-button"
            >
              <LocateFixed className="h-5 w-5 text-[#E11D2A]" aria-hidden="true" />
              {geoLoading ? "Standort wird ermittelt …" : "Meinen Standort verwenden"}
            </button>
          </div>
          {geoMessage && (
            <p className="mt-3 text-sm text-amber-600" role="status" data-testid="finder-geo-message">
              {geoMessage}
            </p>
          )}
        </div>

        <div className="mt-10" data-testid="finder-results">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" aria-hidden="true" data-testid="finder-skeleton">
              {[0, 1, 2].map((i) => (
                <div key={i} className="overflow-hidden rounded-xl border border-[#E4E4E7] bg-white shadow-sm">
                  <div className="aspect-[16/9] bg-[#F4F4F5]" />
                  <div className="space-y-3 p-6">
                    <div className="h-5 w-2/3 rounded bg-[#F4F4F5]" />
                    <div className="h-4 w-1/3 rounded bg-[#F4F4F5]" />
                    <div className="h-4 w-full rounded bg-[#F4F4F5]" />
                    <div className="h-11 w-40 rounded-lg bg-[#F4F4F5]" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#E4E4E7] bg-white p-8 text-center" data-testid="finder-empty">
              <p className="text-[#1A1A1A]">Kein Standort gefunden.</p>
              <p className="mt-1 text-sm text-[#52525B]">Prüfe die Eingabe oder sieh dir alle Standorte an.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {results.map((loc, i) => (
                <div key={loc.slug}>
                  <LocationCard location={loc} distanceKm={loc._distance} titleAs="h2" eager={i === 0} tone="bright" />
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </section>
    </>
  );
}
